import { Reducer } from 'redux';
import { produce } from 'immer';

import {
  ADD_USER_FAILED,
  ADD_USER_REQUEST,
  ADD_USER_SUCCESS,
  DELETE_USER_FAILED,
  DELETE_USER_REQUEST,
  DELETE_USER_SUCCESS,
  GET_USER_FAILED,
  GET_USER_SUCCESS,
  GET_USERS_FAILED,
  GET_USERS_REQUEST,
  GET_USERS_SUCCESS,
  UPDATE_USER_FAILED,
  UPDATE_USER_REQUEST,
  UPDATE_USER_SUCCESS,
  USER_MANAGEMENT_UPDATE_USER_IMAGE,
  RESET_USERS,
  CHANGE_ACTIVE_STATUS_USER_REQUEST,
  CHANGE_ACTIVE_STATUS_USER_SUCCESS,
  CHANGE_ACTIVE_STATUS_USER_FAILED,
} from '../constants/actions';
import { mapKeyToData } from '../../../utils';
import { User } from '../../../interfaces/user.interface';
import { PaginationI } from '../../../interfaces/pagination.interface';
import { ThunkFailAction, ThunkSuccessAction } from '../../../services/apiMiddleware';
import { StoreI } from '../../../rootReducer';
import lStorage from '../../../services/localStorage.service';
import { LocalStorageKeys } from '../../../interfaces/localSotrage.types';
import { fetchExports } from '../../../store/exports/action.types';
import { AttributeI } from '../../../interfaces/attributes.types';

export interface UserManagementState {
  loading: boolean;
  addUserLoading: boolean;
  updateUserLoading: boolean;
  deleteLoading: boolean;
  changingActiveStatusLoading: boolean;
  userDetails: User | null;
  errors: Record<string, any> | null;
  byId: { [key: string]: User } | null;
  pagination: PaginationI | null;
  attributes: AttributeI[];
}

export function getInitialUserManagementState(): UserManagementState {
  return {
    loading: false,
    addUserLoading: false,
    updateUserLoading: false,
    deleteLoading: false,
    changingActiveStatusLoading: false,
    userDetails: null,
    errors: null,
    byId: null,
    pagination: null,
    attributes: [],
  };
}

function addUser(state: UserManagementState, action: ThunkSuccessAction | ThunkFailAction) {
  const {
    response: { data },
  } = action;
  return {
    [data.uuid]: data,
    ...state.byId,
  };
}

function deleteUser(state: UserManagementState, action: ThunkSuccessAction | ThunkFailAction) {
  if (state.byId && state.byId[action.userId]) {
    const tmpUsers = { ...state.byId };
    lStorage.removeItem(LocalStorageKeys.OneTimeNotifications, `${fetchExports.Success}.${action.userId}`);
    if (tmpUsers[action.userId]) {
      delete tmpUsers[action.userId];
    }
    return tmpUsers;
  }
  return state.byId;
}

function updateUser(state: UserManagementState, data: User) {
  if (state.byId) {
    return { ...state.byId, [data.uuid]: data };
  }
  return state.byId;
}

function updateUserImage(state: UserManagementState, data: User): { [key: string]: User } | null {
  if (state.byId) {
    return {
      ...state.byId,
      [data.uuid]: {
        ...state.byId[data.uuid],
        imageUrl: data.imageUrl,
      },
    };
  }
  return state.byId;
}

export function getUsers(state: StoreI): User[] | null {
  const userList = state.userManagement.byId;
  if (userList) {
    return Object.values(userList);
  }
  return null;
}

export function getAttributes(state: StoreI) {
  const attributeList = state.attributes.byId;
  if (attributeList) {
    return Object.values(attributeList);
  }
  return [];
}

const userManagementReducer: Reducer<UserManagementState, ThunkSuccessAction | ThunkFailAction> = produce(
  (draft: UserManagementState, action: ThunkSuccessAction | ThunkFailAction) => {
    switch (action.type) {
      case GET_USER_SUCCESS:
        draft.deleteLoading = false;
        draft.userDetails = action.response.data;
        break;

      case GET_USER_FAILED:
        break;

      case GET_USERS_REQUEST:
        draft.loading = true;
        break;

      case GET_USERS_SUCCESS:
        draft.byId = action.loadMore
          ? { ...draft.byId, ...mapKeyToData(action.response.data.items, 'uuid') }
          : mapKeyToData(action.response.data.items, 'uuid');
        draft.pagination = action.response.data.pagination;
        draft.loading = false;
        break;

      case GET_USERS_FAILED:
        draft.loading = false;
        break;

      case DELETE_USER_REQUEST:
        draft.deleteLoading = true;
        break;

      case DELETE_USER_SUCCESS:
        draft.deleteLoading = false;
        draft.byId = deleteUser(draft, action);
        break;

      case DELETE_USER_FAILED:
        draft.deleteLoading = false;
        break;

      case ADD_USER_REQUEST:
        draft.addUserLoading = true;
        break;

      case ADD_USER_SUCCESS:
        draft.addUserLoading = false;
        draft.byId = addUser(draft, action);
        break;

      case ADD_USER_FAILED:
        draft.addUserLoading = false;
        break;

      case CHANGE_ACTIVE_STATUS_USER_REQUEST:
        draft.changingActiveStatusLoading = true;
        break;

      case CHANGE_ACTIVE_STATUS_USER_SUCCESS:
        draft.changingActiveStatusLoading = false;
        draft.byId = updateUser(draft, action.response.data);
        break;

      case CHANGE_ACTIVE_STATUS_USER_FAILED:
        draft.changingActiveStatusLoading = false;
        draft.errors = action.payload;
        break;

      case UPDATE_USER_REQUEST:
        draft.updateUserLoading = true;
        break;

      case UPDATE_USER_SUCCESS:
        draft.updateUserLoading = false;
        draft.byId = updateUser(draft, action.response.data);
        break;

      case UPDATE_USER_FAILED:
        draft.updateUserLoading = false;
        break;

      case RESET_USERS:
        draft.byId = {};
        break;

      case USER_MANAGEMENT_UPDATE_USER_IMAGE:
        draft.loading = false;
        draft.byId = updateUserImage(draft, action.data);
    }
  },
  getInitialUserManagementState() as UserManagementState,
);

export default userManagementReducer;
